import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import FullscreenError from '@/components/utils/FullscreenError';
import SymbolPriceChart from '@/components/ui/SymbolPriceChart';
import {
  faSpinner,
  faChartLine,
  faArrowLeft,
  faCalendarAlt,
  faRefresh,
  faTrash,
  faChevronLeft,
  faChevronRight
} from '@/fontawesome';
import { useUser } from '@/contexts/UserContext';
import { handleAPIError, showSuccessToast, showErrorToast } from '@/utils/toasts';
import { formatDate, formatCurrency } from '@/utils/helpers';
import { APIRoutes } from '@/services/api.routes';
import { PAGINATION } from '@/config';
import { SYMBOL_DATA_SOURCE_LABELS } from '@/models';
import type { Symbol, SymbolHistoryEntry, SymbolID } from '@/models';
import type { GraphPoint } from '@/modelsAPI';

const pageSize = 50;

export default function AdminSymbolDetail() {
  const { isAdmin } = useUser();
  const { symbolId } = useParams<{ symbolId: SymbolID }>();
  const [symbol, setSymbol] = useState<Symbol | null>(null);
  const [history, setHistory] = useState<SymbolHistoryEntry[]>([]);
  const [graphPoints, setGraphPoints] = useState<GraphPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [graphLoading, setGraphLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [criticalError, setCriticalError] = useState<boolean>(false);
  const [clearingHistory, setClearingHistory] = useState(false);
  const [updatingData, setUpdatingData] = useState(false);
  
  useEffect(() => {
    if (symbolId) {
      fetchSymbolDetails();
    }
  }, [symbolId]);

  const fetchChartGraph = async () => {
    if (!symbolId) return;

    try {
      setGraphLoading(true);
      // Fetch last 90 days of data for the chart
      const start = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString();
      const end = new Date().toISOString();
      const chartResponse = await APIRoutes.getSymbolGraph(symbolId, {
        start,
        end,
        points: PAGINATION.CHART.DETAIL_POINTS
      });
      setGraphPoints(chartResponse.points);
    } catch (err) {
      console.error('Error loading chart graph:', err);
      handleAPIError(err);
    } finally {
      setGraphLoading(false);
    }
  };

  const fetchSymbolDetails = async () => {
    if (!symbolId) return;

    try {
      setLoading(true);
      setError(null);
      setCriticalError(false);

      const [symbolResponse, historyResponse] = await Promise.all([
        APIRoutes.getSymbol(symbolId),
        APIRoutes.listSymbolHistory(symbolId, { limit: pageSize }),
        fetchChartGraph()
      ]);

      setSymbol(symbolResponse);
      setHistory(historyResponse.history);
      setHistoryTotal(historyResponse.total);
      setTotalPages(Math.ceil(historyResponse.total / pageSize));
      setCurrentPage(1);
    } catch (err) {
      console.error('Critical error loading symbol details:', err);
      setError('Backend nicht erreichbar oder Fehler beim Laden der Symbol-Details');
      setCriticalError(true);
      handleAPIError(err);
    } finally {
      setLoading(false);
    }
  };

  const loadPage = async (page: number) => {
    if (!symbolId || historyLoading || page < 1 || page > totalPages) return;

    try {
      setHistoryLoading(true);
      const response = await APIRoutes.listSymbolHistory(symbolId, {
        limit: pageSize,
        next: `$${page}`
      });

      setHistory(response.history);
      setCurrentPage(page);
    } catch (err) {
      console.error('Error loading history page:', err);
      handleAPIError(err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleClearHistory = async () => {
    if (!symbolId || !symbol) return;

    if (!confirm(`Sind Sie sicher, dass Sie die gesamte Historie für ${symbol.ticker} löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.`)) {
      return;
    }

    try {
      setClearingHistory(true);
      await APIRoutes.clearSymbolHistory(symbolId);
      setHistory([]);
      setGraphPoints([]);
      setHistoryTotal(0);
      setCurrentPage(1);
      setTotalPages(1);
      showSuccessToast('Historie erfolgreich gelöscht');
    } catch (err) {
      console.error('Error clearing history:', err);
      showErrorToast('Fehler beim Löschen der Historie');
      handleAPIError(err);
    } finally {
      setClearingHistory(false);
    }
  };

  const handleUpdateData = async () => {
    if (!symbolId || !symbol) return;

    try {
      setUpdatingData(true);
      const r = await APIRoutes.updateSymbolData(symbolId);

      await fetchSymbolDetails();

      if (r.changed) {
        showSuccessToast('Daten aktualisiert (mit Änderungen!)');
      } else {
        showSuccessToast('Daten aktualisiert (keine Änderungen)');
      }

    } catch (err) {
      console.error('Error updating symbol data:', err);
      showErrorToast('Fehler beim Aktualisieren der Symboldaten');
      handleAPIError(err);
    } finally {
      setUpdatingData(false);
    }
  };

  // Show fullscreen error if critical error occurred
  if (criticalError) {
    return (
      <FullscreenError
        title="Symbol-Details nicht verfügbar"
        message={error || 'Die Symbol-Details konnten nicht geladen werden. Möglicherweise ist das Backend nicht erreichbar.'}
        onRetry={fetchSymbolDetails}
        onBack={() => window.history.back()}
      />
    );
  }

  if (loading || !symbol) {
    return (
      <>
        <div className="flex justify-center items-center py-12">
          <FontAwesomeIcon icon={faSpinner} className="w-8 h-8 text-cyan-600 dark:text-cyan-400 animate-spin" />
        </div>
      </>
    );
  }

  return (
    <>
      <div className="flex items-center justify-between mb-8">
          <div className="flex items-center">
            <Link
              to="/admin/symbols"
              className="mr-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <FontAwesomeIcon icon={faArrowLeft} className="w-5 h-5" />
            </Link>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              {symbol.ticker}
            </h1>
          </div>
          <div className="flex items-center space-x-2">
            {/* Action buttons can be added here */}
          </div>
        </div>

        {/* Symbol Overview Card */}
        <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            Symbol-Übersicht
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div>
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Ticker</dt>
              <dd className="mt-1 text-lg font-semibold text-gray-900 dark:text-white">{symbol.ticker}</dd>
            </div>

            <div>
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Börse</dt>
              <dd className="mt-1 text-lg font-semibold text-gray-900 dark:text-white">
                {symbol.exchange !== null ? (
                  <span title={symbol.exchange.name}>{symbol.exchange.identifier}</span>
                ) : (
                  <span className="text-gray-400 dark:text-gray-500">-</span>
                )}
              </dd>
            </div>

            {symbol.currentData && (
              <>
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Aktueller Kurs</dt>
                  <dd className="mt-1 text-lg font-semibold text-gray-900 dark:text-white">
                    {formatCurrency(symbol.currentData.price, symbol.currentData.currency)}
                  </dd>
                </div>

                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Änderung</dt>
                  <dd className={`mt-1 text-lg font-semibold ${ (symbol.currentData.change ?? 0) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400' }`}>
                    {symbol.currentData.change !== null && symbol.currentData.changePct !== null ? `${symbol.currentData.change >= 0 ? '+' : ''}${symbol.currentData.change.toFixed(2)} (${symbol.currentData.changePct.toFixed(2)}%)` : '-'}
                  </dd>
                </div>

                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Marktkapitalisierung</dt>
                  <dd className="mt-1 text-lg font-semibold text-gray-900 dark:text-white">
                    {formatCurrency(symbol.currentData.marketCap, symbol.currentData.currency)}
                  </dd>
                </div>
              </>
            )}

            <div>
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Letzte Aktualisierung</dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                {symbol.lastUpdated ? formatDate(symbol.lastUpdated) : 'Noch nie'}
              </dd>
            </div>

            <div>
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Erstellt</dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                {formatDate(symbol.created)}
              </dd>
            </div>
          </div>

          {symbol.currentData && (
            <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Zusätzliche Daten
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                <div>
                  <dt className="text-gray-500 dark:text-gray-400">Eröffnungskurs</dt>
                  <dd className="text-gray-900 dark:text-white">{formatCurrency(symbol.currentData.priceOpen, symbol.currentData.currency)}</dd>
                </div>
                <div>
                  <dt className="text-gray-500 dark:text-gray-400">Vortagesschluss</dt>
                  <dd className="text-gray-900 dark:text-white">{formatCurrency(symbol.currentData.closeYest, symbol.currentData.currency)}</dd>
                </div>
                <div>
                  <dt className="text-gray-500 dark:text-gray-400">Tageshoch</dt>
                  <dd className="text-gray-900 dark:text-white">{formatCurrency(symbol.currentData.high, symbol.currentData.currency)}</dd>
                </div>
                <div>
                  <dt className="text-gray-500 dark:text-gray-400">Tagestief</dt>
                  <dd className="text-gray-900 dark:text-white">{formatCurrency(symbol.currentData.low, symbol.currentData.currency)}</dd>
                </div>
                <div>
                  <dt className="text-gray-500 dark:text-gray-400">52W Hoch</dt>
                  <dd className="text-gray-900 dark:text-white">{formatCurrency(symbol.currentData.high52, symbol.currentData.currency)}</dd>
                </div>
                <div>
                  <dt className="text-gray-500 dark:text-gray-400">52W Tief</dt>
                  <dd className="text-gray-900 dark:text-white">{formatCurrency(symbol.currentData.low52, symbol.currentData.currency)}</dd>
                </div>
                <div>
                  <dt className="text-gray-500 dark:text-gray-400">Volumen</dt>
                  <dd className="text-gray-900 dark:text-white">{symbol.currentData.volume?.toLocaleString('de-DE') ?? '-'}</dd>
                </div>
                <div>
                  <dt className="text-gray-500 dark:text-gray-400">Ø Volumen</dt>
                  <dd className="text-gray-900 dark:text-white">{symbol.currentData.volumeAvg?.toLocaleString('de-DE') ?? '-'}</dd>
                </div>
                <div>
                  <dt className="text-gray-500 dark:text-gray-400">KGV (P/E)</dt>
                  <dd className="text-gray-900 dark:text-white">{symbol.currentData.pe?.toFixed(2) ?? '-'}</dd>
                </div>
                <div>
                  <dt className="text-gray-500 dark:text-gray-400">EPS</dt>
                  <dd className="text-gray-900 dark:text-white">{formatCurrency(symbol.currentData.eps, symbol.currentData.currency)}</dd>
                </div>
                <div>
                  <dt className="text-gray-500 dark:text-gray-400">Beta</dt>
                  <dd className="text-gray-900 dark:text-white">{symbol.currentData.beta?.toFixed(2) ?? '-'}</dd>
                </div>
                <div>
                  <dt className="text-gray-500 dark:text-gray-400">Aktien im Umlauf</dt>
                  <dd className="text-gray-900 dark:text-white">{symbol.currentData.shares?.toLocaleString('de-DE') ?? '-'}</dd>
                </div>
                <div>
                  <dt className="text-gray-500 dark:text-gray-400">Währung</dt>
                  <dd className="text-gray-900 dark:text-white">{symbol.currentData?.currency ?? '-'}</dd>
                </div>
                <div>
                  <dt className="text-gray-500 dark:text-gray-400">Datenverzögerung</dt>
                  <dd className="text-gray-900 dark:text-white">{symbol.currentData.dataDelay !== null ? `${symbol.currentData.dataDelay} Min.` : '-'}</dd>
                </div>
                <div>
                  <dt className="text-gray-500 dark:text-gray-400">Letzter Handel</dt>
                  <dd className="text-gray-900 dark:text-white">{symbol.currentData?.tradeTime ?? '-'}</dd>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Price Chart Section */}
        <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg p-6 mb-8">
          <div className="flex items-center mb-6">
            <FontAwesomeIcon icon={faChartLine} className="w-5 h-5 text-gray-600 dark:text-gray-400 mr-2" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Kursverlauf
            </h2>
            {graphLoading && (
              <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 text-cyan-600 dark:text-cyan-400 animate-spin ml-2" />
            )}
          </div>
          <SymbolPriceChart
            points={graphPoints}
            currency={symbol.currentData?.currency || 'EUR'}
            height={400}
          />
        </div>

        {/* History Section */}
        <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg overflow-hidden">
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <FontAwesomeIcon icon={faCalendarAlt} className="w-5 h-5 text-gray-600 dark:text-gray-400 mr-2" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Preis-Historie ({historyTotal})
              </h2>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={handleUpdateData}
                disabled={!isAdmin || updatingData}
                title={isAdmin ? 'Manuelles Update triggern' : 'Nur für Administratoren'}
                className={`px-3 py-2 text-sm bg-cyan-600 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center ${!isAdmin ? 'cursor-not-allowed' : 'hover:bg-cyan-700 cursor-pointer'}`}
              >
                {updatingData ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 mr-2 animate-spin" />
                    Aktualisiere...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faRefresh} className="w-4 h-4 mr-2" />
                    Manuelles Update triggern
                  </>
                )}
              </button>
              {historyTotal > 0 && (
                <button
                  onClick={handleClearHistory}
                  disabled={!isAdmin || clearingHistory}
                  title={isAdmin ? 'Historie löschen' : 'Nur für Administratoren'}
                  className={`px-3 py-2 text-sm bg-red-600 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center ${!isAdmin ? 'cursor-not-allowed' : 'hover:bg-red-700 cursor-pointer'}`}
                >
                  {clearingHistory ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 mr-2 animate-spin" />
                      Lösche...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faTrash} className="w-4 h-4 mr-2" />
                      Historie löschen
                    </>
                  )}
                </button>
              )}
            </div>
          </div>

          {history.length === 0 ? (
            <div className="text-center py-12">
              <FontAwesomeIcon icon={faCalendarAlt} className="w-16 h-16 text-gray-400 dark:text-gray-600 mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Keine Historie verfügbar
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Für dieses Symbol sind noch keine historischen Daten vorhanden.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Aufgezeichnet am
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Kurs
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Änderung
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Volumen
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Marktkapitalisierung
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Quelle
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {history.map((entry) => (
                    <tr key={entry.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {formatDate(entry.timestamp)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {formatCurrency(entry.data.price, entry.data.currency)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`text-sm ${
                          (entry.data.change ?? 0) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                        }`}>
                          {entry.data.change !== null && entry.data.changePct !== null ? `${entry.data.change >= 0 ? '+' : ''}${entry.data.change.toFixed(2)} (${entry.data.changePct.toFixed(2)}%)` : '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {entry.data.volume?.toLocaleString('de-DE') ?? '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {formatCurrency(entry.data.marketCap, entry.data.currency)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          entry.source === 'IB'
                            ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                            : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                        }`}>
                          {SYMBOL_DATA_SOURCE_LABELS[entry.source] ?? entry.source}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      Seite {currentPage} von {totalPages} ({historyTotal} Einträge gesamt)
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => loadPage(currentPage - 1)}
                        disabled={currentPage === 1 || historyLoading}
                        className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <FontAwesomeIcon icon={faChevronLeft} className="w-4 h-4" />
                      </button>

                      {/* Page Numbers */}
                      <div className="flex items-center space-x-1">
                        {/* Show first page */}
                        {currentPage > 3 && (
                          <>
                            <button
                              onClick={() => loadPage(1)}
                              disabled={historyLoading}
                              className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer disabled:opacity-50"
                            >
                              1
                            </button>
                            {currentPage > 4 && (
                              <span className="px-2 text-gray-500 dark:text-gray-400">...</span>
                            )}
                          </>
                        )}

                        {/* Show pages around current */}
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          const page = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                          if (page > totalPages) return null;

                          return (
                            <button
                              key={page}
                              onClick={() => loadPage(page)}
                              disabled={historyLoading}
                              className={`px-3 py-2 text-sm border rounded-lg transition-colors cursor-pointer disabled:opacity-50 ${
                                page === currentPage
                                  ? 'bg-cyan-600 text-white border-cyan-600'
                                  : 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                              }`}
                            >
                              {page}
                            </button>
                          );
                        })}

                        {/* Show last page */}
                        {currentPage < totalPages - 2 && (
                          <>
                            {currentPage < totalPages - 3 && (
                              <span className="px-2 text-gray-500 dark:text-gray-400">...</span>
                            )}
                            <button
                              onClick={() => loadPage(totalPages)}
                              disabled={historyLoading}
                              className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer disabled:opacity-50"
                            >
                              {totalPages}
                            </button>
                          </>
                        )}
                      </div>

                      <button
                        onClick={() => loadPage(currentPage + 1)}
                        disabled={currentPage === totalPages || historyLoading}
                        className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <FontAwesomeIcon icon={faChevronRight} className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {historyLoading && (
                    <div className="flex justify-center mt-4">
                      <FontAwesomeIcon icon={faSpinner} className="w-5 h-5 text-cyan-600 dark:text-cyan-400 animate-spin" />
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
    </>
  );
}
